/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation} from 'react-router-dom'
import {useIntl, FormattedMessage} from 'react-intl'
import {isBundleSet, getBundleSetInfo} from '../../utils/bundle-utils'

const ORDER_REVIEW_EASE = [0.22, 1, 0.36, 1]
const ORDER_REVIEW_TRANSITION = {
    enter: {
        height: {duration: 0.3, ease: ORDER_REVIEW_EASE},
        opacity: {duration: 0.35, delay: 0.1, ease: 'easeOut'},
    },
    exit: {
        height: {duration: 0.3, ease: ORDER_REVIEW_EASE},
        opacity: {duration: 0.1, delay: 0.3, ease: 'easeOut'},
    },
}

import {
    Flex,
    Button,
    Skeleton,
    Box,
    Text,
    VStack,
    Fade,
    Collapse,
    useMultiStyleConfig,
    Divider,
} from '@chakra-ui/react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {AccordionBlock} from '../editorial-components'
import {useAddToCartModalContext} from '../../hooks/use-add-to-cart-modal'
import Link from '@salesforce/retail-react-app/app/components/link'

// project components
import Benefits from '../benefits'
import OnlyAtStore from '../only-at-store'
import Breadcrumb from '../breadcrumb'
import ImageGallery, {Skeleton as ImageGallerySkeleton} from '../image-gallery'
import {InformativeLabelGroup} from '../informative-label'
import {BRAND_KEY} from '../../brand'

import ProductHeader, {Skeleton as ProductHeaderSkeleton} from './product-header'
import ProductVariants from './product-variants'
import ProductUpgrade from './product-upgrade'
import ProductVariantsRecommendations from './product-variants-recommendations'
import ProductCare from './product-care'
import KlarnaWidget from '../klarna-widget'
import PaypalWidget from '../paypal-widget'
import {ADD_TO_CART_ERROR_MESSAGES} from '../../constants'
import {API_ERROR_MESSAGE} from '@salesforce/retail-react-app/app/constants'
import {useToast} from '../../hooks/use-toast'
import WhereToBuyModal from '../modals/whereToBuyModal'
import StockLabel from '../stock-label'
import {
    InfoIcon,
    CloseIcon,
    InfoLabelIcon,
    CloseLabelIcon,
    CareShieldIcon,
    AccessoriesCountIcon,
    ProductCountIcon,
    AddIcon,
    MinusIcon,
} from '../icons'
import {buildWarrantyName, getPriceData, isProductPurchasable} from '../../utils/product-utils'
import PriceCard from '../price-card'
import {remCalc} from '../../theme/_utils'
import {useDerivedProduct} from '../../hooks/use-derived-product'
import {
    useCustomPreference,
    useBooleanCustomPreference,
    useCustomPreferenceWithFallback,
} from '../../hooks/use-custom-preference'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import NotifyMeButton from '../notify-me-button'
import {item_gtm_event, polish_ecommerce_gtm_event} from '../../custom-api/layers/default'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {isServer, parseAndSanitizeJSONWithSpaces, noop} from '../../utils/utils'
import {getDamBaseUrl} from '../../custom-hooks/use-dam-image'
import EditorialComponentsFactory from '../editorial-components/editorial-components-factory'
import useCorporateSale from '../../hooks/use-corporate-sale'
import {useMaxQuantityItems} from '../../hooks/use-max-quantity-items'
import ProductDetailBannerBlock from '../product-detail-banner/product-detail-banner-block'

import {getOrderAvailabilityStatus} from '../../utils/order-availability-utils'
import useSpecifications from '../../utils/specifications-utils'
import FlexibleBundle from '../bundle/partials/flexible-bundle'
import FixedBundles from '../bundle/partials/fixed-bundles'
import DynamicIcon from '../dynamic-icon/DynamicIcon'
import {getTechSpecIconId} from '../../utils/dynamicIconHelper'

const ProductViewHeader = ({category, isCategoryLoading}) => {
    const style = useMultiStyleConfig('ProductView')

    return (
        <>
            {isCategoryLoading || category?.length === 0 ? (
                <Box>
                    <Skeleton
                        my={{base: remCalc(20), md: remCalc(16)}}
                        h={{base: remCalc(32), md: remCalc(24)}}
                        w={{base: '100%', md: remCalc(350)}}
                        borderRadius="full"
                    />
                </Box>
            ) : (
                <Box {...style.breadcrumbContainer}>
                    <Breadcrumb categories={category} />
                </Box>
            )}
        </>
    )
}

ProductViewHeader.propTypes = {
    name: PropTypes.string,
    shortDescription: PropTypes.string,
    currency: PropTypes.string,
    category: PropTypes.array,
    priceData: PropTypes.object,
    product: PropTypes.object,
    isProductPartOfBundle: PropTypes.bool,
    addToWishlist: PropTypes.func,
    pdpPlusData: PropTypes.shape(),
    updateWishlist: PropTypes.func,
    isWishlistLoading: PropTypes.bool,
    isProductPartOfSet: PropTypes.bool,
    isCategoryLoading: PropTypes.bool,
}

//Context to avoid prop drilling the images alt text
export const ProductImageAltTextContext = React.createContext()

/**
 * Render a product detail view that includes name, image gallery, price,
 * variant selections, action buttons
 */

const ProductView = forwardRef(
    (
        {
            product,
            category,
            imageSize = 'md',
            isWishlistLoading = false,
            addToCart,
            updateCart,
            addToWishlist,
            updateWishlist,
            isProductLoading,
            isProductPartOfSet = false,
            isProductPartOfBundle = false,
            childOfBundleQuantity = 0,
            childProductOrderability,
            setChildProductOrderability,
            isBasketLoading = false,
            isCategoryLoading = false,
            onVariantSelected = () => {},
            validateOrderability = (variant, quantity, stockLevel) =>
                !isProductLoading && quantity > 0 && quantity <= stockLevel,
            showImageGallery = true,
            // setSelectedBundleQuantity = () => {},
            selectedBundleParentQuantity = 1,
            isCorporate,
            pdpPlusData,
            isBraun = false,
            onFocusLinkClick,
            specificationsRef,
            onAddToCartLoadingChange,
            // Full primary-product document for bundle PDPs
            //  Used to source technical specifications from the
            // bundle's first primary product per requirement.
            // Falls back to `product` when absent (non-bundle PDPs,
            // and other ProductView usages such as the quick-view modal).
            editorialProduct,
            setIsSelectionPending = noop,
            isSelectionPending,
            onOrderReviewPriceDataChange,
        },
        ref,
    ) => {
        const imageGalleryStyles = useMultiStyleConfig('ImageGallery')
        const {currency: activeCurrency} = useCurrency()
        const showToast = useToast()
        const intl = useIntl()
        const location = useLocation()
        const {
            isOpen: isAddToCartModalOpen,
            onOpen: onAddToCartModalOpen,
            onClose: onAddToCartModalClose,
        } = useAddToCartModalContext()
        const [showOptionsMessage, toggleShowOptionsMessage] = useState(false)
        const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false)
        const [selectedAccessoryProducts, setSelectedAccessoryProducts] = useState([])
        const [selectedWarrantyProducts, setSelectedWarrantyProducts] = useState([])

        const bundleSetInfo = getBundleSetInfo(product)
        const isBundleSetProduct = isBundleSet(product)
        const isFixedBundlePdp = isBundleSetProduct && product?.c_bundleType === 'Fixed'
        /*  const isFlexibleBundlePdp = isBundleSetProduct && product?.c_bundleType === 'Flexible' */
        const hasFixedBundle =
            product?.c_isBundle === 'true' &&
            !isBundleSetProduct &&
            product?.c_bundleProductsInfo?.fixedBundles?.length > 0
        const fixedBundles = hasFixedBundle ? product?.c_bundleProductsInfo?.fixedBundles : []
        // Computed eagerly so a `bundleid` query param is already reflected
        // in the very first render markup, both during SSR (e.g. `&__server_only`, which never
        // hydrates and therefore never runs effects) and on the client.
        const getMainProductSelectionFromUrl = () => {
            const bundleIdParam = new URLSearchParams(location.search).get('bundleid')
            const matchedBundle = bundleIdParam
                ? fixedBundles.find((bundle) => bundle.productSetID === bundleIdParam)
                : null

            return matchedBundle
                ? {type: 'fixedBundle', id: matchedBundle.productSetID}
                : {type: 'single', id: product?.id ?? null}
        }
        const [mainProductSelection, setMainProductSelection] = useState(
            getMainProductSelectionFromUrl,
        )
        const [selectedBundleAccessories, setSelectedBundleAccessories] = useState([])
        useEffect(() => {
            setSelectedBundleAccessories([])
        }, [product?.id])
        const selectedBundleAccessoriesCount = selectedBundleAccessories.reduce(
            (sum, item) => sum + item.quantity,
            0,
        )
        const isBundleSelectionComplete =
            isBundleSetProduct &&
            (isFixedBundlePdp ||
                (Number(bundleSetInfo?.bundleMaxSelectable || 0) > 0 &&
                    selectedBundleAccessoriesCount ===
                        Number(bundleSetInfo?.bundleMaxSelectable || 0)))
        const isBundleOrderable =
            isBundleSetProduct &&
            bundleSetInfo?.primaryProducts?.length > 0 &&
            bundleSetInfo.primaryProducts.every(
                (primary) => primary.orderable && (!isFixedBundlePdp || primary.stock > 0),
            )
        const firstPrimaryProduct = isBundleSetProduct
            ? bundleSetInfo?.primaryProducts?.find(
                  (primary) => primary.productId === bundleSetInfo.firstPrimaryProductId,
              ) || bundleSetInfo?.primaryProducts?.[0]
            : null
        const techSpecsSourceProduct =
            isBundleSetProduct && editorialProduct ? editorialProduct : product
        const selectedFixedBundle =
            mainProductSelection?.type === 'fixedBundle'
                ? fixedBundles.find((bundle) => bundle.productSetID === mainProductSelection.id)
                : null
        // Keeps the selection (and the URL) in sync on the client when `product`/`bundleid`
        // change without a full remount (the initial render is already handled above).
        useEffect(() => {
            if (!product?.id) {
                return
            }

            const nextSelection = getMainProductSelectionFromUrl()
            const nextSelectionKey = `${nextSelection.type}:${nextSelection.id}`

            if (lastSelectItemKeyRef.current === nextSelectionKey) {
                return
            }

            setMainProductSelection(nextSelection)
            lastSelectItemKeyRef.current = nextSelectionKey
        }, [product?.id, location.search, fixedBundles])

        const cartAddButtonRef = useRef(null)
        const notifyMeContainerRef = useRef(null)
        const buttonContainerRef = useRef(null)
        const lastSelectItemKeyRef = useRef(null)

        const buildSelectItemPayload = (item) => {
            const itemPrice = Number(item?.price ?? item?.c_variants?.[0]?.price ?? 0)
            const taxRate = Number(item?.c_taxRate ?? 0)
            const stock = Number(item?.stock ?? item?.inventory?.stockLevel ?? 0)

            return {
                item_name: item?.c_defaultName || item?.name || item?.productName,
                item_id: item?.productId || item?.id,
                item_category: item?.c_defaultCategoryName || item?.c_categoryName,
                item_variant: item?.c_subModelIdentifier,
                price: +(itemPrice / (1 + taxRate / 100)).toFixed(2),
                fullprice: getPriceData(item)?.isOnSale ? 'reduced' : 'fullprice',
                rating_points: '',
                erp_category: item?.c_materialGroup,
                prdha_category: item?.c_prdha_category,
                stock_status: stock > 0 ? 'in-stock' : 'out-of-stock',
            }
        }

        const sendSelectItemEvent = (items, bundleId, isFixedBundleSelection = false) => {
            if (!items?.length) {
                return
            }

            addLayer(
                item_gtm_event.bind(this, {
                    event: 'ga_event',
                    event_name: 'select_item',
                    bundle: isFixedBundleSelection ? true : false,
                    ...(bundleId && {bundle_id: bundleId}),
                    currency,
                    items: isFixedBundleSelection ? items : items.map(buildSelectItemPayload),
                }),
            )
        }

        const handleMainProductSelectionChange = (selection) => {
            setMainProductSelection(selection)

            // A single click on the option card can reach this handler multiple times
            // (card onClick + the checkbox label/input click and change), so the event
            // is emitted only once per actual selection change.
            const selectionKey = `${selection?.type}:${selection?.id}`
            if (lastSelectItemKeyRef.current === selectionKey) {
                return
            }
            lastSelectItemKeyRef.current = selectionKey

            if (selection?.type === 'fixedBundle') {
                const bundle = fixedBundles.find((item) => item.productSetID === selection.id)
                if (!bundle) {
                    return
                }

                sendSelectItemEvent(
                    [
                        {
                            item_id: bundle.productSetID,
                            item_name: bundle.productSetName,
                            price: bundle.totalBundlePrice,
                            ...(bundle.c_defaultCategoryName && {
                                item_category: bundle.c_defaultCategoryName,
                            }),
                            fullprice:
                                bundle.standardTotalPrice > bundle.totalBundlePrice
                                    ? 'reduced'
                                    : 'fullprice',
                            rating_points: '',
                            stock_status: bundle.productSetIsOutOfStock
                                ? 'out-of-stock'
                                : 'in-stock',
                        },
                    ],
                    bundle.productSetID,
                    true,
                )
                return
            }

            sendSelectItemEvent([product])
        }

        const handleBundleAccessoriesChange = (nextAccessories) => {
            const resolvedAccessories =
                typeof nextAccessories === 'function'
                    ? nextAccessories(selectedBundleAccessories)
                    : nextAccessories

            const addedAccessories = (resolvedAccessories || []).filter((accessory) => {
                const previous = selectedBundleAccessories.find(
                    (item) => item.productId === accessory.productId,
                )
                return !previous || accessory.quantity > previous.quantity
            })

            sendSelectItemEvent(addedAccessories, bundleSetInfo?.productSetID)
            setSelectedBundleAccessories(resolvedAccessories)
        }
        const renderActionButtonsRef = useRef(null)

        /*         const selectedBundleAccessoriesTotal = selectedBundleAccessories.reduce(
            (sum, item) => sum + item.price * item.quantity,
            0,
        ) */

        const [isOrderReviewOpen, setIsOrderReviewOpen] = useState(true)
        const notShoppableCountry = useBooleanCustomPreference('notShoppableCountry')
        const {data: basket} = useCurrentBasket()
        const {addLayer} = useGoogleTagManager()
        const {currency} = useCurrency()
        const InfoStockIcon = BRAND_KEY === 'braun' ? InfoLabelIcon : InfoIcon
        const OutOfStockIcon = BRAND_KEY === 'braun' ? CloseLabelIcon : CloseIcon
        const OutOfStockVariant = BRAND_KEY === 'braun' ? 'braun-out-of-stock' : 'outOfStock'
        const LastFewItemsVariant = BRAND_KEY === 'braun' ? 'braun-last-few-items' : 'lastFewItems'

        const accordionData = useSpecifications(techSpecsSourceProduct)

        const specificationsTitle = intl.formatMessage({
            id: 'product_detail.specifications.title',
            defaultMessage: 'Specifications',
        })

        const instructionsAccordionItem = {
            accordion_key: 'instructions',
            accordion_title: intl.formatMessage({
                id: 'product_detail.specifications.instructions.title',
                defaultMessage: 'Instructions',
            }),
            values: [
                {
                    value_name: intl.formatMessage({
                        id: 'product_detail.specifications.instructions.subtitle',
                        defaultMessage: 'Instructions subtitle',
                    }),

                    value: (
                        <Text>
                            <FormattedMessage
                                id="product_detail.specifications.instructions.text"
                                defaultMessage="User manual and quick-start guide available in the {support} section"
                                values={{
                                    support: (
                                        <Button
                                            as={Link}
                                            to={`/s/${product?.c_subModelIdentifier}`}
                                            variant="link-button-no-underline"
                                            size="base"
                                            display="inline"
                                            height="auto"
                                            minWidth="auto"
                                            verticalAlign="baseline"
                                            whiteSpace="normal"
                                        >
                                            <FormattedMessage
                                                id="product_detail.specifications.instructions.link"
                                                defaultMessage="support"
                                            />
                                        </Button>
                                    ),
                                }}
                            />
                        </Text>
                    ),
                    has_check: false,
                },
            ],
        }
        const accordionDataWithPlaceholder = [...(accordionData || []), instructionsAccordionItem]

        const {isCorporateSale, corporateSaleLabelName} = useCorporateSale(product)
        const {isQuantityAllowed, getMaxQuantityErrorMessage, getMaxQuantity} =
            useMaxQuantityItems()

        const {
            showLoading,
            showInventoryMessage,
            inventoryMessage,
            quantity,
            // minOrderQuantity,
            setQuantity,
            variant,
            variationParams,
            variationAttributes,
            stockLevel,
            // stepQuantity,
            isOutOfStock,
            unfulfillable,
        } = useDerivedProduct(product, isProductPartOfSet, isProductPartOfBundle)

        const priceData = useMemo(() => {
            return getPriceData(product, {quantity})
        }, [product, quantity])

        const {upgradeRecommended, accessoriesRecommended, warrantiesRecommended} = useMemo(() => {
            const recommendations = product?.c_recommendations || []

            return {
                upgradeRecommended: recommendations.filter(
                    (rec) => rec?.recommendationType?.value === 6,
                ),
                accessoriesRecommended: recommendations.filter(
                    (rec) => rec?.recommendationType?.value === 2,
                ),
                warrantiesRecommended: recommendations.filter(
                    (rec) => rec?.recommendationType?.value === 5,
                ),
            }
        }, [product?.c_recommendations])

        const selectedRecommendationProducts = useMemo(() => {
            const productsById = new Map()
            const parentProductId = variant?.productId || product?.id

            const taggedWarrantyProducts = selectedWarrantyProducts.map((item) => ({
                ...item,
                // Some catalog products (e.g. accessories) already carry their own c_isWarranty
                // custom attribute for unrelated reasons, so a non-c_ marker is used here to avoid
                // misclassifying them as the warranty selection downstream.
                __isWarrantySelection: true,
                c_parentProductId: parentProductId,
                c_UUIDWarranty: item?.c_UUIDWarranty,
            }))

            const mergedProducts = [...selectedAccessoryProducts, ...taggedWarrantyProducts]
            mergedProducts.forEach((item) => {
                if (item?.id && !productsById.has(item.id)) {
                    productsById.set(item.id, item)
                }
            })

            const arrayFromValues = Array.from(productsById.values())
            return arrayFromValues
        }, [product?.id, selectedAccessoryProducts, selectedWarrantyProducts, variant?.productId])

        const priceDataWithRecommendations = useMemo(() => {
            const toCents = (value) => Math.round(Number(value || 0) * 100)

            const selectedRecommendationsPriceCents = selectedRecommendationProducts.reduce(
                (sum, item) => sum + toCents(item?.price || item?.c_variants?.[0]?.price || 0),
                0,
            )

            const selectedRecommendationsListPriceCents = selectedRecommendationProducts.reduce(
                (sum, item) => {
                    const currentPriceCents = toCents(
                        item?.price || item?.c_variants?.[0]?.price || 0,
                    )
                    const listPriceCents = toCents(
                        item?.priceMax || item?.listPrice || currentPriceCents / 100,
                    )
                    return sum + listPriceCents
                },
                0,
            )

            const baseCurrentPrice = selectedFixedBundle
                ? selectedFixedBundle.totalBundlePrice
                : priceData?.currentPrice || 0

            const baseListPrice = selectedFixedBundle
                ? selectedFixedBundle.standardTotalPrice
                : priceData?.listPrice || priceData?.currentPrice || 0

            const currentPriceCents = toCents(baseCurrentPrice) + selectedRecommendationsPriceCents
            const listPriceCents = toCents(baseListPrice)
            const updatedListPriceCents = listPriceCents + selectedRecommendationsListPriceCents

            return {
                ...priceData,
                currentPrice: currentPriceCents / 100,
                listPrice: updatedListPriceCents / 100,
                isOnSale: updatedListPriceCents > currentPriceCents,
            }
        }, [priceData, selectedRecommendationProducts, selectedFixedBundle])

        // TODO: FARE UN HOOK PER I PREZZI E PER I BUNDLE IN GENERALE, POI DIRE A MARIA DI FARLO PER IL RESTO
        const bundlePriceData = useMemo(() => {
            if (!isBundleSetProduct || !bundleSetInfo) {
                return null
            }

            const toCents = (value) => Math.round(Number(value || 0) * 100)

            const primaryCurrentPriceCents = (bundleSetInfo.primaryProducts || []).reduce(
                (sum, primary) => sum + toCents(primary.price),
                0,
            )

            const primaryListPriceCents = (bundleSetInfo.primaryProducts || []).reduce(
                (sum, primary) => sum + toCents(primary.standardPrice || primary.price),
                0,
            )

            const selectedAccessoriesCurrentPriceCents = (selectedBundleAccessories || []).reduce(
                (sum, accessory) =>
                    sum + toCents(accessory.price) * Number(accessory.quantity || 1),
                0,
            )

            const selectedAccessoriesListPriceCents = (selectedBundleAccessories || []).reduce(
                (sum, accessory) =>
                    sum +
                    toCents(accessory.standardPrice || accessory.price) *
                        Number(accessory.quantity || 1),
                0,
            )

            const hasSelectedAccessories = selectedBundleAccessories.length > 0

            const currentPriceCents =
                primaryCurrentPriceCents + selectedAccessoriesCurrentPriceCents
            const listPriceCents = primaryListPriceCents + selectedAccessoriesListPriceCents

            return {
                ...priceData,
                currentPrice: currentPriceCents / 100,
                listPrice: listPriceCents / 100,
                isOnSale: listPriceCents > currentPriceCents,
                isASet: !isFixedBundlePdp && !hasSelectedAccessories,
                isRange: !isFixedBundlePdp && !hasSelectedAccessories,
            }
        }, [
            isBundleSetProduct,
            isFixedBundlePdp,
            bundleSetInfo,
            selectedBundleAccessories,
            priceData,
        ])

        const orderReviewPriceData = isBundleSetProduct
            ? bundlePriceData
            : priceDataWithRecommendations

        useEffect(() => {
            onOrderReviewPriceDataChange?.(orderReviewPriceData)
        }, [orderReviewPriceData])

        const mainPurchaseItems = useMemo(() => {
            if (isBundleSetProduct && bundleSetInfo) {
                const productSetID = bundleSetInfo.productSetID

                const bundleName = bundleSetInfo.productSetName

                const primaryItems = (bundleSetInfo.primaryProducts || []).map(
                    (primaryProduct) => ({
                        product: primaryProduct,
                        variant: {
                            productId: primaryProduct.productId,
                            price: primaryProduct.price,
                        },
                        quantity: 1,
                        orderable: primaryProduct.orderable,
                        stock: primaryProduct.stock,
                        cartCustomFields: {
                            c_productSetID: productSetID,
                            c_bundleName: bundleName,
                            c_isBundleRequest: true,
                        },
                    }),
                )

                const accessoryItems = (selectedBundleAccessories || []).map((accessory) => ({
                    product: accessory,
                    variant: {
                        productId: accessory.productId,
                        price: accessory.price,
                    },
                    quantity: accessory.quantity || 1,
                    orderable: accessory.orderable,
                    stock: accessory.stock,
                    cartCustomFields: {
                        c_productSetID: productSetID,
                        c_bundleName: bundleName,
                        c_isBundleRequest: true,
                        c_isOptionalAccessory: true,
                    },
                }))

                return [...primaryItems, ...accessoryItems]
            }

            if (selectedFixedBundle) {
                return (selectedFixedBundle.productSetProducts || []).map((bundleProduct) => ({
                    product: bundleProduct,
                    variant: {
                        productId: bundleProduct.productId,
                        price: bundleProduct.price,
                    },
                    quantity: 1,
                    orderable: bundleProduct.orderable,
                    stock: bundleProduct.stock,
                    cartCustomFields: {
                        c_productSetID: selectedFixedBundle.productSetID,
                        c_bundleName: selectedFixedBundle.productSetName,
                        c_isBundleRequest: true,
                    },
                }))
            }

            return [
                {
                    product,
                    variant,
                    quantity,
                    orderable: product?.inventory?.orderable,
                    stock: stockLevel ?? product?.inventory?.stockLevel,
                    cartCustomFields: {},
                },
            ]
        }, [
            selectedFixedBundle,
            product,
            variant,
            quantity,
            stockLevel,
            isBundleSetProduct,
            bundleSetInfo,
            selectedBundleAccessories,
        ])
        const isMainPurchaseOrderable = mainPurchaseItems.every(
            (item) => item.orderable && item.stock > 0,
        )
        /*         const mainPurchaseStock = mainPurchaseItems.length
            ? Math.min(...mainPurchaseItems.map((item) => Number(item.stock || 0)))
            : 0 */
        const orderReviewMainItemName = useMemo(() => {
            if (selectedFixedBundle) {
                return selectedFixedBundle.productSetName || ''
            }
            return (
                variant?.c_variantIdentifier ||
                product?.c_variantIdentifier ||
                product?.name ||
                product?.productName ||
                ''
            )
        }, [
            selectedFixedBundle,
            variant?.c_variantIdentifier,
            product?.c_variantIdentifier,
            product?.name,
            product?.productName,
        ])

        // TODO : DA CAPIRE SE GLI ACCESSORI DEL BUNDLE DEVONO ESSERE CONSIDERATI NEL COUNT DEGLI ACCESSORI SELEZIONATI
        const selectedAccessoriesCount = isBundleSetProduct
            ? selectedBundleAccessoriesCount
            : selectedAccessoryProducts.length

        const orderReviewMainItemLabel = useMemo(() => {
            if (!orderReviewMainItemName) {
                return ''
            }
            return `1x ${orderReviewMainItemName}`
        }, [orderReviewMainItemName, product?.type?.bundle])

        const orderReviewCareLabel = useMemo(() => {
            // Show the selected warranty option name in order review.
            const selectedWarranty = selectedWarrantyProducts?.[0]
            const selectedWarrantyGroupName =
                selectedWarranty?.warrantyGroupName || selectedWarranty?.name || ''
            const selectedWarrantyDuration = Number(
                selectedWarranty?.warrantyExtensionPeriod || selectedWarranty?.serviceDuration || 0,
            )

            if (selectedWarrantyGroupName && selectedWarrantyDuration > 0) {
                return buildWarrantyName(
                    selectedWarrantyGroupName,
                    selectedWarrantyDuration,
                    intl.formatMessage,
                )
            }

            if (selectedWarranty?.name) {
                return selectedWarranty.name
            }

            const firstWarranty = warrantiesRecommended?.[0]
            const firstWarrantyVariant = firstWarranty?.variants?.[0]
            const extensionMonths = Number(firstWarrantyVariant?.warrantyExtensionPeriod || 0)

            if (extensionMonths > 0) {
                return buildWarrantyName(
                    firstWarranty?.warrantyGroupName || "De'Longhi Care",
                    extensionMonths,
                    intl.formatMessage,
                )
            }

            if (firstWarranty?.warrantyGroupName) {
                return firstWarranty.warrantyGroupName
            }

            return "De'Longhi Care"
        }, [selectedWarrantyProducts, warrantiesRecommended])

        const isProductASet = product?.type?.set && !isBundleSetProduct
        const isProductABundle = product?.type?.bundle

        const errorContainerRef = useRef(null)
        const style = useMultiStyleConfig('ProductView')
        const handleWishlistItem = async () => {
            if (!updateWishlist && !addToWishlist) return null
            if (updateWishlist) {
                updateWishlist(product, variant, quantity)
                return
            }
            addToWishlist(product, variant, quantity)
        }
        const [isModalOpen, setIsModalOpen] = useState(false)
        const buttonRef = useRef(null)
        const modalAddToCart = useRef(isAddToCartModalOpen)
        const isAddToCartInFlightRef = useRef(false)

        useEffect(() => {
            onAddToCartLoadingChange?.(!isServer && (isBasketLoading || isLoadingAddToCart))
        }, [isBasketLoading, isLoadingAddToCart, onAddToCartLoadingChange])

        const {customInventoryMessage} = useMemo(() => {
            // Check if product is available for preorder or backorder.
            const orderStatus = getOrderAvailabilityStatus(product)
            const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder

            // If product is preorder or backorder, don't show any inventory messages
            let shouldDisableButton = isPreorderOrBackorder ? false : showInventoryMessage
            let shouldShowInventory = isPreorderOrBackorder ? false : showInventoryMessage
            let currentInventoryMsg = ''

            if (
                !shouldDisableButton &&
                (isProductASet || isProductABundle) &&
                childProductOrderability
            ) {
                // if any of the children are not orderable, it will disable the add to cart button
                const unavailableChildProductKey = Object.keys(childProductOrderability).find(
                    (key) => {
                        return childProductOrderability[key].showInventoryMessage
                    },
                )
                shouldDisableButton = !!unavailableChildProductKey
                if (unavailableChildProductKey) {
                    const unavailableChildProduct =
                        childProductOrderability[unavailableChildProductKey]
                    if (unavailableChildProduct.unfulfillable) {
                        currentInventoryMsg = intl.formatMessage(
                            ADD_TO_CART_ERROR_MESSAGES.INVENTORY_REMAINING_FOR_PRODUCT,
                            {
                                stockLevel: unavailableChildProduct.stockLevel,
                                productName: unavailableChildProduct.productName,
                            },
                        )
                    }
                    if (unavailableChildProduct.isOutOfStock) {
                        currentInventoryMsg = intl.formatMessage(
                            ADD_TO_CART_ERROR_MESSAGES.OUT_OF_STOCK_FOR_PRODUCT,
                            {productName: unavailableChildProduct.productName},
                        )
                    }
                }
            }
            return {
                disableButton: shouldDisableButton,
                customInventoryMessage: currentInventoryMsg,
                shouldShowInventoryMessage: shouldShowInventory,
            }
        }, [showInventoryMessage, childProductOrderability, product])

        // Check if product is preorder/backorder to allow button even if not orderable
        const orderStatus = getOrderAvailabilityStatus(product)
        const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder
        const isBackorderCase = orderStatus.isBackorder && !orderStatus.isPreorder
        const isOutOfStockCase =
            (isBundleSetProduct ? isOutOfStock : product?.inventory?.stockLevel === 0) &&
            !orderStatus.isPreorder &&
            !orderStatus.isBackorder
        const shouldShowNotifyMe =
            !isBundleSetProduct && (orderStatus.isPreorder || isBackorderCase || isOutOfStockCase)
        const buttonDisabled =
            !isPreorderOrBackorder &&
            !product?.inventory?.orderable &&
            !product?.c_isPDPWhereToBuy &&
            !isBundleSetProduct
        const mainPurchaseButtonDisabled =
            buttonDisabled ||
            (selectedFixedBundle && !isMainPurchaseOrderable) ||
            (isBundleSetProduct && (!isBundleOrderable || !isBundleSelectionComplete))
        const isVariationGroup =
            product?.hitType === 'variation_group' || !!product?.type?.variationGroup

        const validateAndShowError = (opts = {}) => {
            const {scrollErrorIntoView = true} = opts

            // Skip validation for preorder/backorder products
            const orderStatus = getOrderAvailabilityStatus(product)
            const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder

            if (isPreorderOrBackorder) {
                toggleShowOptionsMessage(false)
                return true
            }
            // Fixed bundles require every member; flexible bundles also require accessory selection.
            if (isBundleSetProduct) {
                const isValidBundle = isBundleOrderable && isBundleSelectionComplete

                toggleShowOptionsMessage(false)

                return isValidBundle
            }

            // Validate that all attributes are selected before proceeding.
            const hasValidSelection = validateOrderability(variant, quantity, stockLevel)
            const showError = !isProductASet && !isProductABundle && !hasValidSelection
            const scrollToError = showError && scrollErrorIntoView

            toggleShowOptionsMessage(showError)

            if (scrollToError) {
                errorContainerRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                })
            }

            return hasValidSelection
        }

        useEffect(() => {
            if (modalAddToCart.current === true && isAddToCartModalOpen === false) {
                requestAnimationFrame(() => {
                    buttonRef.current?.focus()
                })
            }
            modalAddToCart.current = isAddToCartModalOpen
        }, [isAddToCartModalOpen])

        const renderActionButtons = () => {
            if (!product) {
                return []
            }
            const buttons = []
            const buttonText = {
                update: intl.formatMessage({
                    defaultMessage: 'Update',
                    id: 'product_view.button.update',
                }),
                addToCart: intl.formatMessage({
                    defaultMessage: 'Add to Cart',
                    id: 'global.add-to-cart',
                }),
                preorderNow: intl.formatMessage({
                    defaultMessage: 'Preorder now',
                    id: 'global.preorder_now',
                }),
                addSetToCart: intl.formatMessage({
                    defaultMessage: 'Add Set to Cart',
                    id: 'product_view.button.add_set_to_cart',
                }),
                addBundleToCart: intl.formatMessage({
                    defaultMessage: 'Add Bundle to Cart',
                    id: 'product_view.button.add_bundle_to_cart',
                }),
                whereToBuy: intl.formatMessage({
                    defaultMessage: 'Where to buy',
                    id: 'product_view.button.where_to_buy',
                }),
            }

            const addToCartEventPDP = async () => {
                const isSelectedFixedBundle = Boolean(selectedFixedBundle)
                const bundleId = isBundleSetProduct
                    ? product?.id
                    : selectedFixedBundle?.productSetID

                const productPrice = isBundleSetProduct
                    ? bundlePriceData?.currentPrice
                    : isSelectedFixedBundle
                    ? selectedFixedBundle?.totalBundlePrice
                    : priceData?.currentPrice
                await addLayer(polish_ecommerce_gtm_event.bind(this))
                await addLayer(
                    item_gtm_event.bind(this, {
                        event: 'ga_event',
                        event_name: 'add_to_cart',
                        currency: currency,
                        complete_experience: selectedAccessoryProducts.length > 0,
                        delonghi_care: selectedWarrantyProducts?.length > 0 ? 'basic' : '',
                        bundle: isBundleSetProduct || isSelectedFixedBundle ? 'true' : 'false',
                        ...(bundleId && {bundle_id: bundleId}),
                        items: [
                            {
                                item_name: product?.c_defaultName || product?.name,
                                item_id: isSelectedFixedBundle ? bundleId : product?.id,
                                item_category:
                                    product?.c_defaultCategoryName ||
                                    product?.c_breadcrumbs?.[0]?.name,
                                item_variant: product?.c_subModelIdentifier,
                                price:
                                    isBundleSetProduct || isSelectedFixedBundle
                                        ? productPrice
                                        : parseFloat(
                                              (
                                                  product?.price /
                                                  (1 + parseFloat(product?.c_taxRate) / 100)
                                              ).toFixed(2),
                                          ),
                                fullprice: getPriceData(product)?.isOnSale
                                    ? 'reduced'
                                    : 'fullprice',
                                rating_points: '',
                                // get_reevoo_rating() || get_bv_rating(),
                                erp_category: product?.c_materialGroup,
                                prdha_category: product?.c_prdha_category,
                                quantity: quantity,
                            },
                            ...selectedAccessoryProducts.map((accessory) => {
                                const accessoryPrice =
                                    accessory?.price || accessory?.c_variants?.[0]?.price

                                return {
                                    item_name: accessory?.c_defaultName || accessory?.name,
                                    item_id:
                                        accessory?.c_variants?.[0]?.variationValues
                                            ?.skuIdentifier ||
                                        accessory?.c_variants?.[0]?.productId ||
                                        accessory?.id,
                                    item_category: 'Complete the experience',
                                    item_variant: accessory?.c_subModelIdentifier,
                                    item_complete_experience: true,
                                    price: parseFloat(
                                        (
                                            accessoryPrice /
                                            (1 + parseFloat(accessory?.c_taxRate) / 100)
                                        ).toFixed(2),
                                    ),
                                    fullprice: getPriceData(accessory)?.isOnSale
                                        ? 'reduced'
                                        : 'fullprice',
                                    rating_points: '',
                                    erp_category: accessory?.c_materialGroup,
                                    prdha_category: accessory?.c_prdha_category,
                                    quantity: 1,
                                }
                            }),
                        ],
                    }),
                )
            }
            const handleCartItem = async () => {
                // temporary fix for flexible bundle max quantity validation, TODO :  remove once the strategy is defined
                if (isAddToCartInFlightRef.current) {
                    return
                }
                isAddToCartInFlightRef.current = true

                try {
                    if (/* !isBundleSetProduct && */ !isQuantityAllowed(product, quantity)) {
                        showToast({
                            title: getMaxQuantityErrorMessage(getMaxQuantity(product)),
                            status: 'error',
                        })
                        setIsLoadingAddToCart(false)
                        return
                    }
                    // check if there is already a customized product in the cart
                    if (basket?.productItems?.[0]?.c_officinaConfigurationId) {
                        return showToast({
                            title: intl.formatMessage({
                                defaultMessage:
                                    'Since you are adding a customized product to your cart, your order cannot include other types of products. Before adding other products to your cart, complete your order.',
                                id: 'officina.error.complete_your_order',
                            }),
                            status: 'error',
                        })
                    }

                    setIsLoadingAddToCart(true)
                    const hasValidSelection = validateAndShowError()

                    if (!hasValidSelection) {
                        setIsLoadingAddToCart(false)
                        return null
                    }

                    if (!addToCart && !updateCart) {
                        setIsLoadingAddToCart(false)
                        return null
                    }
                    if (updateCart) {
                        await updateCart(variant || product, quantity)
                        setIsLoadingAddToCart(false)
                        return
                    }

                    // --- Check if basket already has all stock ---
                    const orderStatus = getOrderAvailabilityStatus(product)
                    const isPreorderOrBackorder = orderStatus.isPreorder || orderStatus.isBackorder

                    // Skip stock check for preorder/backorder as they use custom preference limits
                    // For bundle sets we intentionally skip this legacy stock check.
                    // This check is SKU-based and validates the PDP product/variant against the quantity already in basket.
                    // On a bundle PDP, however, the PDP product is the Product Set container, while the actual
                    // purchasable items are the primary products plus the selected accessories stored in mainPurchaseItems.
                    //
                    // We could use mainPurchaseStock here, which is calculated as the minimum stock across all
                    // mainPurchaseItems, but that would only tell us the theoretical stock available for the current
                    // bundle composition. It would not take into account quantities of the same SKUs that may already
                    // be present in the basket.
                    //
                    // The correct bundle-aware validation should check each item in mainPurchaseItems individually:
                    // basket quantity for that SKU + quantity to add <= item.stock.
                    // Until that multi-item stock validation is implemented, this legacy check must remain limited to
                    // standard PDP products to avoid validating the Product Set container instead of the actual bundle items.
                    if (!isPreorderOrBackorder && !isBundleSetProduct) {
                        const basketQuantity =
                            basket?.productItems
                                ?.filter(
                                    (item) =>
                                        item.productId === (variant?.productId || product?.id),
                                )
                                ?.reduce((sum, item) => sum + item.quantity, 0) || 0
                        const availableStock = stockLevel ?? product?.inventory?.stockLevel ?? 0

                        if (basketQuantity >= availableStock) {
                            showToast({
                                title: intl.formatMessage(
                                    ADD_TO_CART_ERROR_MESSAGES.QUANTITY_EXCEEDS_STOCK,
                                ),
                                status: 'error',
                            })
                            setIsLoadingAddToCart(false)
                            return
                        }
                    }
                    // --- END stock check ---

                    try {
                        const itemsAdded = await addToCart(
                            variant,
                            quantity,
                            selectedRecommendationProducts,
                            selectedFixedBundle || isBundleSetProduct ? mainPurchaseItems : null,
                        )
                        if (itemsAdded) {
                            onAddToCartModalOpen({
                                product,
                                itemsAdded,
                                selectedQuantity: quantity,
                            })
                        }
                    } catch (e) {
                        showToast({
                            title: intl.formatMessage(API_ERROR_MESSAGE),
                            status: 'error',
                        })
                    }
                    addToCartEventPDP()
                } finally {
                    setIsLoadingAddToCart(false)
                    isAddToCartInFlightRef.current = false
                }
            }

            const handleButtonClick = (e) => {
                buttonRef.current = e.currentTarget
                if (product?.inventory?.orderable === false && product?.c_isPDPWhereToBuy) {
                    setIsModalOpen(true)
                } else {
                    handleCartItem()
                }
            }

            const handleCloseModal = () => {
                setIsModalOpen(false)
            }
            // ADD TO CART BUTTON ---> CHANGE THE BEHAVIOR FOR FLEXIBLE BUNDLES , BUT MAINTAIN THE SAME BEHAVIOR FOR OTHER SETS
            // child product of bundles do not have add to cart button
            if (
                ((addToCart || updateCart) &&
                    !isProductPartOfBundle &&
                    !buttonDisabled &&
                    !notShoppableCountry &&
                    !isVariationGroup) ||
                isBundleSetProduct
            ) {
                // Check if product is in preorder status
                const orderStatus = getOrderAvailabilityStatus(product)
                buttons.push(
                    <Button
                        ref={(el) => {
                            buttonRef.current = el
                            cartAddButtonRef.current = el
                        }}
                        className="js-cart-add"
                        data-cs-label="pdp-addToCart"
                        data-product-code={product.id}
                        key="cart-button"
                        onClick={handleButtonClick}
                        isDisabled={mainPurchaseButtonDisabled || isSelectionPending}
                        isLoading={!isServer && (isBasketLoading || isLoadingAddToCart)}
                        width="100%"
                        variant="primary"
                        size="xl"
                        height={remCalc(56)}
                        suppressHydrationWarning
                    >
                        {product?.inventory?.orderable === false && product?.c_isPDPWhereToBuy
                            ? buttonText.whereToBuy
                            : updateCart
                            ? buttonText.update
                            : isProductASet
                            ? buttonText.addSetToCart
                            : isProductABundle
                            ? buttonText.addBundleToCart
                            : orderStatus.isPreorder
                            ? buttonText.preorderNow
                            : buttonText.addToCart}
                    </Button>,
                    <WhereToBuyModal
                        key="where-to-buy-modal"
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                    />,
                )
            }

            return buttons
        }

        renderActionButtonsRef.current = renderActionButtons

        // Bind the reference with our `scope` that includes the internal validate function for this component.
        // Other values can be added to this scope as required.
        if (typeof ref === 'function') {
            ref = ref.bind({
                validateOrderability: validateAndShowError,
                renderActionButtons: (...args) => renderActionButtonsRef.current(...args),
                cartAddButtonRef: cartAddButtonRef,
                notifyMeContainerRef: notifyMeContainerRef,
                shouldShowNotifyMe: shouldShowNotifyMe,
                buttonContainerRef: buttonContainerRef,
            })
        }

        // Set the quantity of bundle child in a product bundle to ensure availability messages appear
        if (
            isProductPartOfBundle &&
            quantity != selectedBundleParentQuantity * childOfBundleQuantity
        ) {
            setQuantity(selectedBundleParentQuantity * childOfBundleQuantity)
        }

        useEffect(() => {
            if (isAddToCartModalOpen) {
                onAddToCartModalClose()
            }
        }, [location.pathname])

        useEffect(() => {
            if (
                !isProductASet &&
                !isProductABundle &&
                validateOrderability(variant, quantity, stockLevel)
            ) {
                toggleShowOptionsMessage(false)
            }
        }, [variationParams])

        useEffect(() => {
            if (variant) {
                onVariantSelected(product, variant, quantity)
            }
        }, [variant?.productId, quantity])

        useEffect(() => {
            if (isProductPartOfBundle || isProductPartOfSet) {
                const key = product.itemId ?? product.id
                // when showInventoryMessage is true, it means child product is not orderable
                setChildProductOrderability((previousState) => ({
                    ...previousState,
                    [key]: {
                        showInventoryMessage,
                        isOutOfStock,
                        unfulfillable,
                        stockLevel,
                        productName:
                            product?.c_variantIdentifier || product?.name || product?.productName,
                    },
                }))
            }
        }, [showInventoryMessage, inventoryMessage])

        const lastItemThreshold = useCustomPreference('showLastItemThreshold')
        const digiohVendorID = useCustomPreference('digiohVendorID')
        const subscriptionRedirectPDP = useCustomPreferenceWithFallback('subscriptionRedirectPDP')

        const subscribeButton = product?.c_recommendations?.find(
            (rec) => rec?.recommendationType?.value === 4,
        ) &&
            subscriptionRedirectPDP && (
                <Button
                    w="100%"
                    mt={remCalc(8)}
                    variant="secondary"
                    as={Link}
                    to={subscriptionRedirectPDP}
                    // onClick={() =>
                    //     onAddToCartModalOpen({
                    //         itemsAdded: [{product, quantity: 1}],
                    //         isSubscription: true,
                    //     })
                    // }
                >
                    <FormattedMessage
                        defaultMessage="Buy in subscription"
                        id="product_item.subscription.button.buy_subscription"
                    />
                </Button>
            )

        const productImageAltText = `${product?.c_categoryName}, ${product?.c_variantIdentifier}, ${product?.variationValues?.skuIdentifier}, ${product?.c_marketingColour}`

        const powerImages =
            typeof product?.c_localizedPowerImages === 'string'
                ? parseAndSanitizeJSONWithSpaces(product?.c_localizedPowerImages)
                : Array.isArray(product?.c_localizedPowerImages)
                ? product?.c_localizedPowerImages
                : []
        const parseMediaItems = (value) => {
            if (!value) return []

            let normalizedItems = []

            if (typeof value === 'string') {
                const trimmedValue = value.trim()
                if (!trimmedValue) return []

                const parsed = parseAndSanitizeJSONWithSpaces(
                    trimmedValue,
                    product?.c_subModelIdentifier,
                )

                if (Array.isArray(parsed)) {
                    normalizedItems = parsed
                } else if (parsed && typeof parsed === 'object') {
                    normalizedItems = [parsed]
                } else {
                    // Support plain DAM path strings like "807x590/assets/287011".
                    normalizedItems = [{imageLink: trimmedValue}]
                }
            } else if (Array.isArray(value)) {
                normalizedItems = value
            }

            return normalizedItems
                .map((item, index) => ({
                    alt: item?.alt || item?.title || `Gallery media ${index + 1}`,
                    title: item?.title || `Gallery media ${index + 1}`,
                    imageLink: item?.imageLink || item?.medialink || item?.url || item?.link,
                    url: item?.imageLink || item?.medialink || item?.url || item?.link,
                }))
                .filter((item) => item.imageLink)
        }
        const damBaseUrl = getDamBaseUrl()
        const withDamBaseUrl = (item) => {
            const fullUrl = item.imageLink?.startsWith('http')
                ? item.imageLink
                : `${damBaseUrl}/${item.imageLink}`
            return {...item, imageLink: fullUrl, url: fullUrl}
        }

        const inTheBoxImages = parseMediaItems(
            product?.c_whatsInThePackImages || product?.c_whatsInTheBox,
        ).map(withDamBaseUrl)
        const featureImages = parseMediaItems(
            product?.c_galleryFeaturesImages ||
                product?.c_galleryFeatures ||
                product?.c_featureImages,
        ).map(withDamBaseUrl)
        const productBannerInfo = product?.c_bannerInfo
        const energyEfficiencyLevel =
            product?.c_energyEfficiencyClass || product?.representedProduct?.c_energyEfficiencyClass
        const energyManualDocument =
            product?.c_manualDocument || product?.representedProduct?.c_manualDocument
        const weeeFeeAmount = Number(
            product?.c_weeeFee ?? product?.representedProduct?.c_weeeFee ?? 0,
        )

        const showStockLabel =
            !showLoading &&
            !isBundleSetProduct &&
            !orderStatus.isPreorder &&
            !orderStatus.isBackorder &&
            (isBundleSetProduct ? isBundleOrderable : product?.inventory?.orderable) &&
            stockLevel <= lastItemThreshold

        return (
            <Flex {...style.flexContainer} data-testid="product-view" ref={ref}>
                <ProductViewHeader category={category} isCategoryLoading={isCategoryLoading} />
                <Flex {...style.productContainer}>
                    {showImageGallery && (
                        <Box
                            {...style.imageGalleryBox}
                            position={{base: 'relative', lg: 'sticky'}}
                            top={{base: 'auto', lg: remCalc(28)}}
                            alignSelf={{base: 'stretch', lg: 'flex-start'}}
                            h="fit-content"
                            zIndex={{base: 'auto', lg: 1}}
                        >
                            {product && !isProductLoading ? (
                                <>
                                    <Flex {...imageGalleryStyles.infoLabelContainer}>
                                        <InformativeLabelGroup
                                            product={product}
                                            priceData={priceData}
                                            isCorporateSale={isCorporateSale}
                                            corporateSaleLabelName={corporateSaleLabelName}
                                            showOutOfStock={isOutOfStock}
                                        />
                                    </Flex>
                                    <ProductImageAltTextContext.Provider
                                        value={productImageAltText}
                                    >
                                        <ImageGallery
                                            key={product.id}
                                            size={imageSize}
                                            imageGroups={product.imageGroups}
                                            powerImages={powerImages}
                                            inTheBoxImages={inTheBoxImages}
                                            featureImages={featureImages}
                                            selectedVariationAttributes={variationParams}
                                            lazy={isProductPartOfSet || isProductPartOfBundle}
                                            videoUrl={product?.c_youtubeVideoGeneric}
                                            subModelIdentifier={product?.c_subModelIdentifier}
                                        />
                                    </ProductImageAltTextContext.Provider>
                                </>
                            ) : (
                                <ImageGallerySkeleton />
                            )}
                        </Box>
                    )}
                    <Divider {...style.horizontalDivider} />
                    {/* Variations & Quantity Selector & CTA buttons */}
                    <VStack
                        {...style.productCard}
                        data-ab="right-column"
                        data-cs-label="right-column"
                    >
                        <Box {...style.productHeaderContainer}>
                            {isProductLoading ? (
                                <ProductHeaderSkeleton />
                            ) : (
                                <ProductHeader
                                    name={
                                        product?.c_variantIdentifier ||
                                        product?.name ||
                                        product?.productName
                                    }
                                    product={product}
                                    handleWishlistItem={handleWishlistItem}
                                    isWishlistLoading={isWishlistLoading}
                                    firstPrimaryProduct={firstPrimaryProduct}
                                    isFlexibleBundle={isBundleSetProduct}
                                />
                            )}
                        </Box>
                        {isProductLoading ? (
                            <VStack
                                align="stretch"
                                gap={remCalc(16)}
                                w="full"
                                data-cs-label="pdp-colorVariants"
                            >
                                <VStack align="stretch" gap={remCalc(12)} w="100%">
                                    <Skeleton
                                        h={remCalc(20)}
                                        w={{base: '72%', md: '48%'}}
                                        borderRadius="full"
                                    />
                                    <Skeleton h={remCalc(160)} w="100%" borderRadius="2xl" />
                                </VStack>
                                <VStack
                                    align="stretch"
                                    gap={remCalc(12)}
                                    w="100%"
                                    pt={{base: remCalc(8), md: remCalc(16)}}
                                >
                                    <Skeleton
                                        h={remCalc(20)}
                                        w={{base: '72%', md: '48%'}}
                                        borderRadius="full"
                                    />
                                    <Skeleton h={remCalc(320)} w="100%" borderRadius="2xl" />
                                </VStack>
                                <VStack align="stretch" gap={remCalc(10)} w="100%">
                                    <VStack align="flex-start" spacing={1}>
                                        <Skeleton boxSize={remCalc(16)} borderRadius="full" />
                                        <Skeleton
                                            h={remCalc(12)}
                                            w={{base: '28%', md: '24%'}}
                                            borderRadius="full"
                                        />
                                        <Skeleton
                                            h={remCalc(12)}
                                            w={{base: '42%', md: '36%'}}
                                            borderRadius="full"
                                        />
                                    </VStack>
                                    <Skeleton h={remCalc(72)} w="100%" borderRadius="2xl" />
                                </VStack>
                            </VStack>
                        ) : variationAttributes.length || isBundleSetProduct ? (
                            <VStack
                                align="stretch"
                                gap={0}
                                w="full"
                                data-cs-label="pdp-colorVariants"
                            >
                                {isProductPartOfBundle && (
                                    <Box>
                                        <Text fontWeight="medium" fontSize="md" aria-label="price">
                                            <label>
                                                {intl.formatMessage({
                                                    defaultMessage: 'Quantity',
                                                    id: 'product_view.label.quantity',
                                                })}
                                                : {childOfBundleQuantity}
                                            </label>
                                        </Text>
                                    </Box>
                                )}
                                <VStack {...style.recommendationsSections}>
                                    <ProductDetailBannerBlock
                                        productBannerInfo={productBannerInfo}
                                        onFocusLinkClick={onFocusLinkClick}
                                        productName={product?.name}
                                    />
                                    {!isBundleSetProduct && (
                                        <ProductVariants
                                            variationAttributes={variationAttributes}
                                            product={product}
                                            lastItemThreshold={lastItemThreshold}
                                            InfoStockIcon={InfoStockIcon}
                                            OutOfStockIcon={OutOfStockIcon}
                                            LastFewItemsVariant={LastFewItemsVariant}
                                            OutOfStockVariant={OutOfStockVariant}
                                            priceData={priceData}
                                            activeCurrency={activeCurrency}
                                        />
                                    )}
                                    {!isBundleSetProduct && hasFixedBundle && (
                                        <FixedBundles
                                            product={product}
                                            fixedBundles={fixedBundles}
                                            mainProductSelection={mainProductSelection}
                                            onMainProductSelectionChange={
                                                handleMainProductSelectionChange
                                            }
                                        />
                                    )}

                                    {showStockLabel && (
                                        <Box {...style.inventoryLabelContainer}>
                                            <Fade in>
                                                <StockLabel
                                                    variant={LastFewItemsVariant}
                                                    icon={
                                                        <InfoStockIcon circleColor="transparent" />
                                                    }
                                                    textMessage={
                                                        <FormattedMessage
                                                            id="use_product.message.low_stock"
                                                            defaultMessage="Last {quantity} {itemLabel}"
                                                            values={{
                                                                quantity: stockLevel,
                                                                itemLabel:
                                                                    stockLevel === 1 ? (
                                                                        <FormattedMessage
                                                                            id="global.message.item"
                                                                            defaultMessage="item"
                                                                        />
                                                                    ) : (
                                                                        <FormattedMessage
                                                                            id="global.message.items"
                                                                            defaultMessage="items"
                                                                        />
                                                                    ),
                                                            }}
                                                        />
                                                    }
                                                />
                                            </Fade>
                                        </Box>
                                    )}

                                    {upgradeRecommended.length > 0 && (
                                        <ProductUpgrade recommendations={upgradeRecommended} />
                                    )}
                                    {!hasFixedBundle && !isFixedBundlePdp && (
                                        <ProductVariantsRecommendations
                                            recommendations={accessoriesRecommended}
                                            onSelectionChange={setSelectedAccessoryProducts}
                                            setIsSelectionPending={setIsSelectionPending}
                                        />
                                    )}
                                    <ProductCare
                                        recommendations={warrantiesRecommended}
                                        onSelectionChange={setSelectedWarrantyProducts}
                                    />
                                </VStack>

                                <Box ref={errorContainerRef}>
                                    {!showLoading && showOptionsMessage && (
                                        <Fade in={true}>
                                            <Text
                                                color="text.danger"
                                                fontWeight={'semibold'}
                                                marginBottom={8}
                                            >
                                                {intl.formatMessage(
                                                    ADD_TO_CART_ERROR_MESSAGES.NO_VARIANT_SELECTED,
                                                )}
                                            </Text>
                                        </Fade>
                                    )}
                                </Box>
                                {isProductASet && <p>{product?.shortDescription}</p>}
                                {isFixedBundlePdp && bundleSetInfo && (
                                    <FixedBundles
                                        standalone
                                        fixedBundles={[
                                            {
                                                ...bundleSetInfo,
                                                productSetProducts: bundleSetInfo.primaryProducts,
                                                totalBundlePrice: bundlePriceData?.currentPrice,
                                                standardTotalPrice: bundlePriceData?.listPrice,
                                                productSetIsOutOfStock: !isBundleOrderable,
                                            },
                                        ]}
                                    />
                                )}
                                {isBundleSetProduct && !isFixedBundlePdp && (
                                    <FlexibleBundle
                                        flexibleBundleInfo={bundleSetInfo}
                                        selectedAccessories={selectedBundleAccessories}
                                        onSelectAccessories={handleBundleAccessoriesChange}
                                        onFocusLinkClick={onFocusLinkClick}
                                    />
                                )}
                            </VStack>
                        ) : null}
                        <Divider {...style.horizontalDivider} />

                        <Box {...style.priceBox}>
                            {!isProductLoading && (
                                <Box {...style.orderReviewContainer}>
                                    <Box>
                                        <Flex
                                            as="button"
                                            type="button"
                                            aria-expanded={isOrderReviewOpen}
                                            onClick={() =>
                                                setIsOrderReviewOpen((isOpen) => !isOpen)
                                            }
                                            {...style.orderReviewHeader}
                                        >
                                            <Text {...style.orderReviewTitle}>
                                                <FormattedMessage
                                                    id="product_view.order_review.title"
                                                    defaultMessage="Order review"
                                                />
                                            </Text>
                                            <Flex {...style.orderReviewChevron}>
                                                <AddIcon
                                                    {...style.orderReviewChevronIcon}
                                                    {...style.orderReviewToggleIcon}
                                                    opacity={isOrderReviewOpen ? 0 : 1}
                                                    transform={`scale(${
                                                        isOrderReviewOpen ? 0.5 : 1
                                                    })`}
                                                    aria-hidden="true"
                                                />
                                                <MinusIcon
                                                    {...style.orderReviewChevronIcon}
                                                    {...style.orderReviewToggleIcon}
                                                    opacity={isOrderReviewOpen ? 1 : 0}
                                                    transform={`scale(${
                                                        isOrderReviewOpen ? 1 : 0.5
                                                    })`}
                                                    aria-hidden="true"
                                                />
                                            </Flex>
                                        </Flex>

                                        <Collapse
                                            in={isOrderReviewOpen}
                                            unmountOnExit
                                            transition={ORDER_REVIEW_TRANSITION}
                                        >
                                            <VStack {...style.orderReviewItemsList}>
                                                {orderReviewMainItemName && (
                                                    <Flex {...style.orderReviewProductRow}>
                                                        <ProductCountIcon
                                                            {...style.orderReviewProductIcon}
                                                            aria-hidden="true"
                                                        />
                                                        <Text {...style.orderReviewItem}>
                                                            {orderReviewMainItemLabel}
                                                        </Text>
                                                    </Flex>
                                                )}

                                                {selectedAccessoriesCount > 0 && (
                                                    <Flex {...style.orderReviewAccessoryRow}>
                                                        <AccessoriesCountIcon
                                                            {...style.orderReviewAccessoryIcon}
                                                            aria-hidden="true"
                                                        />
                                                        <Text {...style.orderReviewItem}>
                                                            <FormattedMessage
                                                                id="product_view.order_review.accessories"
                                                                defaultMessage="{count}x Accessories"
                                                                values={{
                                                                    count: selectedAccessoriesCount,
                                                                }}
                                                            />
                                                        </Text>
                                                    </Flex>
                                                )}

                                                {selectedWarrantyProducts.length > 0 && (
                                                    <Flex {...style.orderReviewCareRow}>
                                                        <CareShieldIcon
                                                            {...style.orderReviewCareIcon}
                                                            aria-hidden="true"
                                                        />
                                                        <Text {...style.orderReviewItem}>
                                                            {orderReviewCareLabel}
                                                        </Text>
                                                    </Flex>
                                                )}
                                            </VStack>
                                        </Collapse>
                                    </Box>

                                    <Flex {...style.orderReviewPriceRow}>
                                        <Box {...style.orderReviewPriceCard}>
                                            <PriceCard
                                                priceData={orderReviewPriceData}
                                                product={product}
                                                activeCurrency={activeCurrency}
                                                placeInfoPopoverInOmnibus={false}
                                                extractWeeeFromPrice={false}
                                                showWeeeFee={true}
                                                showDisplayPriceInfoWithOmnibus={true}
                                                showOmnibusPrice={
                                                    selectedAccessoriesCount === 0 &&
                                                    !isProductABundle &&
                                                    !selectedFixedBundle
                                                }
                                                energyEfficiencyLevel={energyEfficiencyLevel}
                                                energyManualDocument={energyManualDocument}
                                                weeeFeeAmount={weeeFeeAmount}
                                                onFocusLinkClick={onFocusLinkClick}
                                                variant="productList"
                                            />
                                        </Box>
                                    </Flex>
                                </Box>
                            )}

                            {!showLoading && customInventoryMessage && (
                                <Fade in={true}>
                                    <Text
                                        color="orange.600"
                                        fontWeight={'semibold'}
                                        marginBottom={8}
                                    >
                                        {customInventoryMessage}
                                    </Text>
                                </Fade>
                            )}

                            <Box
                                minHeight={remCalc(40)}
                                data-testid="button-container-pdp"
                                ref={buttonContainerRef}
                            >
                                {(showLoading || isProductPurchasable(product)) && (
                                    <Box
                                        gap={remCalc(8)}
                                        display="flex"
                                        flexDirection="column"
                                        alignItems={'stretch'}
                                        width={'100%'}
                                    >
                                        <Box
                                            display={
                                                buttonDisabled
                                                    ? 'none'
                                                    : isProductPartOfSet
                                                    ? 'block'
                                                    : ['none', 'none', 'none', 'block']
                                            }
                                        >
                                            {renderActionButtons()}
                                            {subscribeButton}
                                        </Box>
                                        <Box
                                            display={
                                                buttonDisabled
                                                    ? 'none'
                                                    : isProductPartOfSet || isProductPartOfBundle
                                                    ? 'none'
                                                    : ['block', 'block', 'block', 'none']
                                            }
                                        >
                                            {renderActionButtons()}
                                            {subscribeButton}
                                        </Box>
                                        {/* Show Notify Me button for preorder, backorder, or out of stock */}
                                        {shouldShowNotifyMe && (
                                            <Box ref={notifyMeContainerRef}>
                                                <NotifyMeButton
                                                    product={product}
                                                    size="xl"
                                                    height={remCalc(56)}
                                                />
                                            </Box>
                                        )}
                                    </Box>
                                )}
                                <Box {...style.priceAndKlarnaBox}>
                                    <Box w="100%">
                                        {!isProductLoading &&
                                        priceDataWithRecommendations?.currentPrice ? (
                                            <>
                                                <KlarnaWidget price={orderReviewPriceData} />
                                                <PaypalWidget
                                                    price={orderReviewPriceData?.currentPrice}
                                                    placement="product"
                                                />
                                            </>
                                        ) : isProductLoading ? (
                                            <Skeleton
                                                h={remCalc(136)}
                                                w="100%"
                                                borderRadius="2xl"
                                            />
                                        ) : (
                                            <></>
                                        )}
                                    </Box>
                                </Box>
                            </Box>

                            <Box {...style.benefitsAndSpecificationsGroup}>
                                <Benefits product={product} orderStatus={orderStatus} />
                                <Flex
                                    flexDir={isBraun ? 'column-reverse' : 'column'}
                                    data-ab="tech-spec"
                                >
                                    <Box ref={specificationsRef}>
                                        <AccordionBlock
                                            title={specificationsTitle}
                                            items={accordionDataWithPlaceholder}
                                            renderLeadingIcon={(item, index) => {
                                                if (item?.accordion_key === 'instructions') {
                                                    return (
                                                        <InfoIcon
                                                            {...style.infoIcon}
                                                            marginRight={remCalc(8)}
                                                        />
                                                    )
                                                }

                                                return (
                                                    <DynamicIcon
                                                        iconId={getTechSpecIconId({
                                                            groupedTechSpecs:
                                                                techSpecsSourceProduct?.c_groupedTechSpecs,
                                                            category: item?.accordion_title,
                                                            index,
                                                            sourceKey: item?.accordion_key,
                                                        })}
                                                        marginRight={remCalc(8)}
                                                    />
                                                )
                                            }}
                                            styleVariant="productView"
                                        />
                                    </Box>
                                </Flex>
                            </Box>

                            <Box>
                                <OnlyAtStore />
                                <Box>
                                    {!isCorporate &&
                                        pdpPlusData?.column_editorial_components?.map(
                                            (component) => (
                                                <EditorialComponentsFactory
                                                    key={component.uid}
                                                    componentType={component?._content_type_uid}
                                                    {...component}
                                                />
                                            ),
                                        )}
                                    <Flex
                                        {...style.benefitsAndBannerBox}
                                        sx={{
                                            '#mcp-pdp-container:empty, #digioh-pdp-container:empty':
                                                {
                                                    display: 'none',
                                                },
                                        }}
                                    >
                                        <Box id="mcp-pdp-container" />
                                        {digiohVendorID && <Box id="digioh-pdp-container" />}
                                    </Flex>
                                </Box>
                            </Box>
                        </Box>
                    </VStack>
                </Flex>
            </Flex>
        )
    },
)

ProductView.displayName = 'ProductView'

ProductView.propTypes = {
    product: PropTypes.object,
    isProductPartOfSet: PropTypes.bool,
    isProductPartOfBundle: PropTypes.bool,
    childOfBundleQuantity: PropTypes.number,
    category: PropTypes.array,
    isProductLoading: PropTypes.bool,
    isBasketLoading: PropTypes.bool,
    isCategoryLoading: PropTypes.bool,
    isWishlistLoading: PropTypes.bool,
    addToCart: PropTypes.func,
    addToWishlist: PropTypes.func,
    isCorporate: PropTypes.bool,
    pdpPlusData: PropTypes.shape(),
    updateCart: PropTypes.func,
    updateWishlist: PropTypes.func,
    showFullLink: PropTypes.bool,
    imageSize: PropTypes.oneOf(['sm', 'md']),
    childProductOrderability: PropTypes.object,
    setChildProductOrderability: PropTypes.func,
    onVariantSelected: PropTypes.func,
    validateOrderability: PropTypes.func,
    showImageGallery: PropTypes.bool,
    setSelectedBundleQuantity: PropTypes.func,
    selectedBundleParentQuantity: PropTypes.number,
    isBraun: PropTypes.bool,
    onFocusLinkClick: PropTypes.func,
    specificationsRef: PropTypes.object,
    onAddToCartLoadingChange: PropTypes.func,
    editorialProduct: PropTypes.object,
    setIsSelectionPending: PropTypes.func,
    isSelectionPending: PropTypes.bool,
    onOrderReviewPriceDataChange: PropTypes.func,
}

export default ProductView
